import { useServices } from '~/composables/useServices';
import type { IRegisterResponse } from '~/interfaces/dto/auth/response';
import { globalKeys } from 'assets/data/global-keys';
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store';
import type { IInfoResponse } from '~/interfaces/dto/marketing/response';
import { brandIds, regionIdRegex } from 'assets/data/config';
import { z } from 'zod';
import { testRegex } from '~/composables/useFormBuilder';
import type {
  IFormBuilderReference,
  IFormBuilderValidator,
} from '~/interfaces/form/formBuilder';
import { generateTranslationKey } from '~/utilities/data-transformers';
import type { IWelcomeOfferClaimPrize } from '~/interfaces/dto/welcome-offers/response';

const interacted = ref<boolean>(false);

const { lockDateOfBirth, unlockDateOfBirth, toggleReferralInputActive } =
  useRegistrationState();

export const validateIdField = (form: Ref<IFormBuilderReference>) => {
  const idNumberType = form.value?.model?.['idNumberType'];
  if (idNumberType === 'idNumber') validateIdCard(form);
  else validateOtherIdentityType(form);
};

export const validateIdCard = (form: Ref<IFormBuilderReference>) => {
  const { $enabled, $dayjs } = useNuxtApp();
  if ($enabled('myaccount.enablepartialreg')) return;
  const { validDob, formattedDate } = getDobDetails(
    form.value.model['idNumber'],
  );

  if (validDob) {
    lockDateOfBirth();

    form.value.model['dateOfBirth'] = $dayjs(formattedDate);

    if (
      form.value.model['idNumber'].length < 6 &&
      form.value.model['idNumber'] !== ''
    ) {
      form.value.model['dateOfBirth'] = null;
    }
  } else {
    form.value.model['dateOfBirth'] = null;
    unlockDateOfBirth();
  }
};

export const assignIdNumberValidator = (regExp: string) => {
  const { $enabled } = useNuxtApp();

  return z.string().superRefine((value, ctx) => {
    if (!testRegex(value, regExp)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'validation.id-number.regex',
        path: ['idNumber'],
      });
    }

    if ($enabled('myaccount.enablepartialreg')) return;

    const { validDob, difference } = getDobDetails(value);

    if (validDob) {
      lockDateOfBirth();
      if (difference < 18) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'formErrors.idNumber-underage',
          path: ['idNumber'],
        });
      }
    } else {
      unlockDateOfBirth();
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'validation.id-number.invalid',
        path: ['idNumber'],
      });
    }
  });
};

const getDobDetails = (value: string) => {
  const { $dayjs } = useNuxtApp();
  const dobRegex =
    '^([0-9]){2}(([0][2]([0][1-9]|[1][0-9]|[2][0-8]))|((([0][469])|([1][1]))(([0][1-9])|([1][0-9])|([2][0-9])|([3][0])))|((([0][13578])|([1][02]))(([0][1-9])|([1][0-9])|([2][0-9])|([3][01]))))([0-9]*)$';
  const leapYearDobRegex =
    '^([0-9]){2}(([0][2]([0][1-9]|[1][0-9]|[2][0-9]))|((([0][469])|([1][1]))(([0][1-9])|([1][0-9])|([2][0-9])|([3][0])))|((([0][13578])|([1][02]))(([0][1-9])|([1][0-9])|([2][0-9])|([3][01]))))([0-9]*)$';
  const regExpression =
    parseInt(value.substring(0, 2)) % 4 !== 0 ? dobRegex : leapYearDobRegex;
  const regex = new RegExp(regExpression);
  const validDob = regex.test(value);
  const birthDateIdentifier = value.substring(0, 6);
  const shortYear = parseInt(birthDateIdentifier.substring(0, 2));
  const year =
    shortYear > 21
      ? `19${shortYear}`
      : shortYear < 10 || shortYear === 0
        ? `200${shortYear}`
        : `20${shortYear}`;
  const month = birthDateIdentifier.substring(2, 4);
  const day = birthDateIdentifier.substring(4, 6);
  const formattedDate = `${year}-${month}-${day}`;
  const date = $dayjs();
  const difference = date.diff(formattedDate, 'year');
  return {
    validDob,
    difference,
    formattedDate,
    regExpression,
    regex,
  };
};
const validateOtherIdentityType = (form: Ref<IFormBuilderReference>) => {
  const site = useSiteStore();
  const idNumberType = form.value?.model?.['idNumberType'];
  const modelVal = form?.value?.model?.['idNumber'];
  form.value.validator['idNumber'] = z
    .string()
    .refine(
      (value) =>
        testRegex(value, regionIdRegex[site.getRegionCode]?.[idNumberType]),
      {
        message: `formErrors.${idNumberType}`,
      },
    );
  const isValid = ref(false);

  isValid.value =
    modelVal === null
      ? false
      : testRegex(modelVal, regionIdRegex[site.getRegionCode]?.[idNumberType]);
  if (isValid.value) {
    delete form.value.formErrors['idNumber'];
  } else {
    form.value.formErrors['idNumber'] = `formErrors.${idNumberType}`;
  }
};

export const focusInputField = (inputName: string) => {
  setTimeout(() => {
    document.getElementById(inputName).focus();
  }, 250);
};

export const handleErrorCodes = (error, form: Ref<IFormBuilderReference>) => {
  const { $t } = useNuxtApp();
  const idType = 'idCard' in form.value.validator ? 'idCard' : 'idNumber';
  switch (error?.code) {
    case 132002:
    case 153008:
      if (form.value.schema.hasSteps) form.value.steps.previous();
      focusInputField('username');
      form.value.formErrors['username'] = $t('number-in-use');
      break;
    case 132035:
    case 153010:
      if (form.value.schema.hasSteps) form.value.steps.previous();
      focusInputField('email');
      form.value.formErrors['email'] = $t('email-in-use');
      break;
    case 132014:
    case 153009:
      focusInputField(idType);
      form.value.formErrors[idType] = $t('id-already-exists');
      break;
    case 132036:
      focusInputField(idType);
      form.value.formErrors[idType] = $t('id-invalid');
      break;
    default:
      console.error(error?.code);
      break;
  }
};

export const handleUnSuccessfulReg = (
  registrationResponse: IRegisterResponse,
  form: Ref<IFormBuilderReference>,
  formError: Ref,
  errorRef: Ref<string>,
) => {
  const { $t } = useNuxtApp();
  const error = registrationResponse?.error;
  formError.value = !error
    ? $t('registration-failed')
    : $t(`jpc-reg-error.${error?.displayCode}`) !==
        `jpc-reg-error.${error?.displayCode}`
      ? $t(`jpc-reg-error.${error?.displayCode}`)
      : error?.message;
  errorRef.value = String(error?.code) || '';
  handleErrorCodes(error, form);
};

export const handleSuccessfulReg = (
  actionId: number,
  registrationResponse: IRegisterResponse,
  formModel: Ref,
  formError: Ref,
) => {
  const { $t, $trackEvent } = useNuxtApp();
  const site = useSiteStore();
  const authStore = useAuthStore();
  const { $complianceService, $storageService, $promotionService } =
    useServices();
  $storageService.removeCookie({
    key: globalKeys.marketingKeys.signUpCode,
  });
  $trackEvent('FormEventEnum', 'FORM_COMPLETE', {
    form_name: 'registration',
  });
  window?.dtrum?.leaveAction(actionId);
  if (
    !$storageService.getCookie({
      key: globalKeys.marketingKeys.bannerTag,
    })
  ) {
    window?.dtrum?.reportCustomError(
      'bannertags',
      `Account ${formModel.value['username']} registered with no btag set`,
      null,
      false,
    );
  }
  if (site.getFeatureFlags.get('website.enablewelcomeoffer')) {
    const registrationPrize: number = $storageService.getSessionStorage({
      key: globalKeys.marketingKeys.welcomeOfferRegPrize,
    });
    if (!!registrationPrize) {
      const request: IWelcomeOfferClaimPrize = {
        prizeNumber: 'FirstPrize',
        prizeSelectionValue: registrationPrize,
        isRegistration: true,
      };
      $promotionService.updateWelcomeOfferStatus(request);
    }
  }
  if (site.getFeatureFlags.get('myaccount.enablecompliance')) {
    if (formModel.value['idNumberType'] === 'passport') {
      authStore.setPartialPostReg(true);
      site.activateModal('registrationWelcomeOffer', registrationResponse);
    } else {
      if (!registrationResponse?.data?.complianceResponse?.isValidId) {
        formError.value = $t('deceased-or-minor-reg');
        $trackEvent('RegistrationEvent', 'REG_FAILURE', {
          msisdn: formModel?.value['username'],
          reason: 'Deceased or Minor Individual Reg',
        });
        $trackEvent('FormEventEnum', 'FORM_ERROR', {
          form_name: 'registration',
          form_error: 'Deceased or Minor Individual Reg',
        });
        throw 'deceased-or-minor-reg';
      } else {
        if (
          registrationResponse?.data?.complianceResponse?.complianceStatus > 0
        ) {
          authStore.setPartialPostReg(false);
          site.activateModal('registrationWelcomeOffer', registrationResponse);
        } else {
          $complianceService.getPostRegNames().then((data) => {
            if (data?.isSuccessful) {
              site.activateModal('registrationVerifyName');
            } else {
              authStore.setPartialPostReg(true);
              site.activateModal(
                'registrationWelcomeOffer',
                registrationResponse,
              );
            }
          });
        }
      }
    }
  } else {
    site.activateModal('registrationWelcomeOffer', registrationResponse);
  }
};

export const changeIdType = (form: Ref<IFormBuilderReference>) => {
  const { $t } = useNuxtApp();
  const site = useSiteStore();
  const idType = 'idCard' in form.value.validator ? 'idCard' : 'idNumber';

  delete form.value.formErrors?.[idType];
  form.value.model[idType] = '';

  form.value.schema.sectionMapping.forEach((section) => {
    section.elementMapping.elements.forEach((element) => {
      if (element.id === idType) {
        element.placeholder = $t(form.value.model['idNumberType']);
        element.label = $t(form.value.model['idNumberType']);
        element.regex =
          regionIdRegex?.[site.getRegionCode]?.[
            form.value.model['idNumberType']
          ];
        element.messages = {
          regex: `validation.${generateTranslationKey(form.value.model['idNumberType'])}.regex`,
        };
        form.value.validator[idType] = z.string().refine(
          (value) => {
            return new RegExp(element.regex).test(value);
          },
          {
            message: `validation.${generateTranslationKey(form.value.model['idNumberType'])}.regex`,
          },
        );
      }
    });
  });

  if (form?.value?.model?.['idNumberType'].toLowerCase() !== idType) {
    unlockDateOfBirth();
    form.value.validator[idType] = z.string().refine(
      (value) => {
        return new RegExp(
          regionIdRegex?.[site.getRegionCode]?.[
            form.value.model['idNumberType']
          ],
        ).test(value);
      },
      {
        message: `validation.${generateTranslationKey(form.value.model['idNumberType'])}.regex`,
      },
    );
  } else {
    form.value.validator[idType] = assignIdNumberValidator(
      regionIdRegex?.[site.getRegionCode]?.idNumber,
    );
  }
};

export const getAdditionalProps = (
  deviceInfo: IInfoResponse,
  formModel: Ref,
  forPartialReg: boolean = false,
  campaign?: string,
) => {
  const site = useSiteStore();
  const { $storageService } = useServices();
  const preparedUsername = usePrepareMobileNumber(formModel?.value?.username);
  return {
    // Reg props
    ...(!forPartialReg && { countryCode: site.region }),
    ...(!forPartialReg && { currencyCode: site.getCurrencyCode }),
    ...(!forPartialReg && { brandId: brandIds[site.getRegionCode] }),
    ...(!forPartialReg && { gender: 'Male' }),
    ...(!forPartialReg && {
      languageOption: formModel.value?.preferredLanguage || 'en-US',
    }),
    ...(!forPartialReg && { communicationChannel: 'Web' }),
    ...(!forPartialReg && {
      idNumberType: formModel?.value?.idNumberType?.replaceAll(' ', ''),
    }),
    ...(!forPartialReg && {
      sessionMetadata: {
        welcomeOffer: formModel?.value?.welcomeOffer || '',
        sessionTrackingToken: deviceInfo?.hash,
        uip: deviceInfo?.IpAddress,
        ...(!!campaign && { campaign }),
      },
    }),

    // Partial reg props
    ...(forPartialReg && { testAccount: false }),
    ...(forPartialReg && { autoCashIn: false }),
    ...(forPartialReg && { receivePromotionalInformation: false }),

    // Dynamic props for both partial and normal reg
    // duplicate tags to prevent issues with naming clashes

    // partial reg
    bannerTag:
      $storageService.getCookie({
        key: globalKeys.marketingKeys.bannerTag,
      }) || 'P97425-PR26623-CM89033-TS1984022',
    sessionToken: $storageService.getCookie({
      key: globalKeys.marketingKeys.sessionToken,
    }),
    masterToken: $storageService.getCookie({
      key: globalKeys.marketingKeys.masterToken,
    }),
    // full reg
    btagCookie:
      $storageService.getCookie({
        key: globalKeys.marketingKeys.bannerTag,
      }) || 'P97425-PR26623-CM89033-TS1984022',
    st: $storageService.getCookie({
      key: globalKeys.marketingKeys.sessionToken,
    }),
    mt: $storageService.getCookie({
      key: globalKeys.marketingKeys.masterToken,
    }),

    // Standard props for both partial and normal reg
    primaryMobileNumber: preparedUsername,
    signUpCode: formModel?.value?.referralCode || '',
    cultureCode: site.getRegion,
    dialingCode: site.getDialingCode,
    originalQueryString: $storageService.getCookie({
      key: globalKeys.marketingKeys.originalQueryString,
    }),
  };
};

export const sourceOfFundsError = (form: IFormBuilderReference) => {
  if ('sourceOfFunds' in form?.validator)
    if (
      !form?.validator['sourceOfFunds'].safeParse(form.model['sourceOfFunds'])
        .success as unknown as IFormBuilderValidator
    )
      form.formErrors['sourceOfFunds'] = 'formErrors.sourceOfFunds';
  if ('idNumber' in form?.validator)
    if (
      !form?.validator['idNumber'].safeParse(form.model['idNumber'])
        .success as unknown as IFormBuilderValidator
    )
      form.formErrors['idNumber'] = 'formErrors.idNumber';
};

export const handleProgressFields = (
  progressBarFields: Ref,
  form: Ref<IFormBuilderReference>,
) =>
  nextTick(() => {
    if (!form.value.model) return;
    const { $storageService } = useServices();
    const route = useRoute();
    progressBarFields.value = { ...form?.value?.validator };
    if (
      (!!route.query?.signupcode ||
        !!$storageService.getCookie({
          key: globalKeys.marketingKeys.signUpCode,
        })) &&
      !form?.value?.model?.['referralCode']
    ) {
      toggleReferralInputActive();
      form.value.model['referralCode'] =
        route.query?.signupcode ||
        $storageService.getCookie({
          key: globalKeys.marketingKeys.signUpCode,
        });
    }
  });

export const trackInitInteraction = () => {
  const { $trackEvent } = useNuxtApp();
  if (!interacted.value) {
    interacted.value = true;
    $trackEvent('RegistrationEvent', 'REG_INTERACTED');
  }
};

export const getActionId = () => {
  const { $storageService } = useServices();
  const route = useRoute();
  return window?.dtrum?.enterAction(
    `registration-[${$storageService.getCookie({ key: globalKeys.marketingKeys.bannerTag })}]`,
    null,
    null,
    route.fullPath,
  );
};
